import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, User } from "lucide-react";
import ImageUploader from "@/components/admin/ImageUploader";

const CATEGORIES = [
  { key: "coach", label: "Coach" },
  { key: "administration", label: "Administration" },
  { key: "support", label: "Support Staff" },
];
const SPORTS = ["", "cricket", "football", "badminton", "swimming", "gym"];
const empty = { name: "", role: "", category: "coach", sport: "", photo_url: "", bio: "", experience_years: "", phone: "", email: "", order: 0, active: true };

export default function AdminTeam() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/team").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...form,
        experience_years: form.experience_years ? parseInt(form.experience_years) : undefined,
        order: parseInt(form.order) || 0,
      };
      if (editing?.id) payload.id = editing.id;
      await api.post("/admin/team", payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!confirm("Delete team member?")) return;
    await api.delete(`/admin/team/${id}`);
    load();
  };
  const openNew = () => { setEditing({}); setForm(empty); };
  const openEdit = (m) => { setEditing(m); setForm({ ...empty, ...m, experience_years: m.experience_years || "" }); };

  const filtered = filter === "all" ? items : items.filter((m) => m.category === filter);

  return (
    <div data-testid="admin-team">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Coaches & Administration</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Team Directory.</h1>
        </div>
        <button onClick={openNew} data-testid="new-team-btn" className="bg-brand-blue text-white px-5 py-2 text-xs uppercase font-bold tracking-widest flex items-center gap-2 hover:glow-blue">
          <Plus size={12} /> New Team Member
        </button>
      </div>

      <div className="flex gap-2 mb-6">
        <FilterBtn v="all" cur={filter} set={setFilter}>All ({items.length})</FilterBtn>
        {CATEGORIES.map((c) => (
          <FilterBtn key={c.key} v={c.key} cur={filter} set={setFilter}>{c.label} ({items.filter((m) => m.category === c.key).length})</FilterBtn>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((m) => (
          <div key={m.id} data-testid={`team-${m.id}`} className="border border-white/12 bg-[#131A2B] p-5 hover-lift">
            <div className="flex gap-4">
              {m.photo_url ? (
                <img src={m.photo_url} alt={m.name} className="w-16 h-16 rounded-full object-cover border border-white/12" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-white/6 border border-white/12 flex items-center justify-center"><User size={20} className="text-white/40" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg uppercase font-bold truncate">{m.name}</div>
                <div className="text-xs font-mono brand-orange uppercase tracking-widest">{m.role}</div>
                {m.sport && <div className="text-[10px] font-mono text-white/40 mt-1">{m.sport} · {m.experience_years}y exp</div>}
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => openEdit(m)} data-testid={`edit-team-${m.id}`} className="p-1 text-white/60 hover:brand-blue"><Edit3 size={14} /></button>
                <button onClick={() => del(m.id)} data-testid={`del-team-${m.id}`} className="p-1 text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
            {m.bio && <p className="text-xs text-white/60 mt-3 line-clamp-2">{m.bio}</p>}
            <div className="mt-3 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest">
              <span className={m.active ? "brand-blue" : "text-white/30"}>● {m.active ? "active" : "inactive"}</span>
              <span className="text-white/40">{m.category}</span>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center py-12 text-white/40">No team members in this filter.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-[#131A2B] border border-white/12 p-8 max-w-xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl uppercase font-black">{editing.id ? "Edit" : "New"} Team Member</h3>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="team-name" className="bg-transparent border border-white/20 py-3 px-4 col-span-2" />
              <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Role (e.g. Head Cricket Coach)" data-testid="team-role" className="bg-transparent border border-white/20 py-3 px-4 col-span-2" />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-transparent border border-white/20 py-3 px-4">
                {CATEGORIES.map((c) => <option key={c.key} value={c.key} className="bg-[#131A2B]">{c.label}</option>)}
              </select>
              <select value={form.sport || ""} onChange={(e) => setForm({ ...form, sport: e.target.value })} className="bg-transparent border border-white/20 py-3 px-4">
                <option value="" className="bg-[#131A2B]">— sport (optional) —</option>
                {SPORTS.slice(1).map((s) => <option key={s} value={s} className="bg-[#131A2B]">{s}</option>)}
              </select>
              <div className="col-span-2">
                <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">Photo</label>
                <ImageUploader
                  value={form.photo_url || ""}
                  onUploaded={(url) => setForm({ ...form, photo_url: url })}
                  onCleared={() => setForm({ ...form, photo_url: "" })}
                  label="Upload team member photo"
                  testid="team-photo"
                />
              </div>
              <textarea value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Bio / description" rows={3} className="bg-transparent border border-white/20 py-3 px-4 col-span-2" />
              <input type="number" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} placeholder="Experience (years)" className="bg-transparent border border-white/20 py-3 px-4" />
              <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} placeholder="Display order" className="bg-transparent border border-white/20 py-3 px-4" />
              <input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className="bg-transparent border border-white/20 py-3 px-4" />
              <input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optional)" className="bg-transparent border border-white/20 py-3 px-4" />
              <label className="flex items-center gap-2 text-sm col-span-2">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active (visible on public site)
              </label>
            </div>
            <button onClick={save} data-testid="team-save" className="mt-6 w-full bg-brand-blue text-white py-3 font-bold uppercase tracking-widest">Save Member</button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterBtn({ v, cur, set, children }) {
  return (
    <button
      onClick={() => set(v)}
      data-testid={`team-filter-${v}`}
      className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border ${cur === v ? "bg-brand-blue text-white border-brand-blue" : "border-white/20 text-white/60 hover:border-white/50"}`}
    >
      {children}
    </button>
  );
}
